import { createTheme } from '@mui/material/styles';

/** Default public surfaces brand (booking, client portal, pay links). */
export const DEFAULT_PUBLIC_BRAND = {
  company_name: 'WorkWiseSoft',
  logo_url: '',
  primary_color: '#1565C0',
  accent_color: '#42A5F5',
  background_color: '#F0F4F8',
  header_gradient: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 55%, #42A5F5 100%)',
};

function normalizeHex(value) {
  if (!value || typeof value !== 'string') return null;
  let v = value.trim();
  if (!v) return null;
  if (v[0] !== '#') v = `#${v}`;
  if (!/^#[0-9A-Fa-f]{6}$/.test(v)) return null;
  return v.toUpperCase();
}

export function darkenHex(hex, amount = 0.35) {
  const h = hex.replace('#', '');
  const r = Math.max(0, Math.round(parseInt(h.slice(0, 2), 16) * (1 - amount)));
  const g = Math.max(0, Math.round(parseInt(h.slice(2, 4), 16) * (1 - amount)));
  const b = Math.max(0, Math.round(parseInt(h.slice(4, 6), 16) * (1 - amount)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
}

/** Merge API payload with defaults. */
export function resolvePublicBrand(api) {
  const b = api && typeof api === 'object' ? api : {};
  const primary = normalizeHex(b.primary_color) || DEFAULT_PUBLIC_BRAND.primary_color;
  const accent = normalizeHex(b.accent_color) || DEFAULT_PUBLIC_BRAND.accent_color;
  const background = normalizeHex(b.background_color) || DEFAULT_PUBLIC_BRAND.background_color;
  const headerGradient =
    (typeof b.header_gradient === 'string' && b.header_gradient.trim()) ||
    `linear-gradient(135deg, ${darkenHex(primary)} 0%, ${primary} 55%, ${accent} 100%)`;

  return {
    company_name: String(b.company_name || '').trim() || DEFAULT_PUBLIC_BRAND.company_name,
    logo_url: String(b.logo_url || '').trim(),
    primary_color: primary,
    accent_color: accent,
    background_color: background,
    header_gradient: headerGradient,
  };
}

/** MUI + inline styles for gradient header (sx `background` alone is unreliable). */
export function brandHeaderSx(brand) {
  const b = resolvePublicBrand(brand);
  return {
    backgroundColor: b.primary_color,
    backgroundImage: b.header_gradient,
    color: '#fff',
  };
}

/** Inline style backup so gradients always paint. */
export function brandHeaderStyle(brand) {
  const b = resolvePublicBrand(brand);
  return {
    backgroundColor: b.primary_color,
    backgroundImage: b.header_gradient,
    color: '#fff',
  };
}

/** Shape used by public booking UI. */
export function toBookingBrand(api) {
  const b = resolvePublicBrand(api);
  return {
    primary: b.primary_color,
    primaryDark: darkenHex(b.primary_color, 0.2),
    bg: b.background_color,
    headerGradient: b.header_gradient,
    companyName: b.company_name,
    logoUrl: b.logo_url,
  };
}

export function brandPageSx(brand) {
  const b = resolvePublicBrand(brand);
  return {
    minHeight: '100vh',
    bgcolor: b.background_color,
  };
}

export function createPublicBrandTheme(brand) {
  const b = resolvePublicBrand(brand);
  return createTheme({
    palette: {
      primary: {
        main: b.primary_color,
        dark: darkenHex(b.primary_color, 0.15),
        contrastText: '#ffffff',
      },
    },
  });
}
